/**
 * Sahneye gecici hata ayiklama mesh'leri (kure/kup) ekleyen denetleyici.
 *
 * Engine.log / Engine.clearDebugLog buraya delege eder. Eklenen mesh'ler tek bir
 * "engine-debug-log" grubunda toplanir; clear hepsini ve kaynaklarini (geometry/
 * material) dispose eder.
 */
import {
  BoxGeometry,
  Color,
  Euler,
  Group,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  SphereGeometry,
  Vector3,
} from "three";
import type { Engine } from "../Engine";

export class DebugLogController {
  private _group?: Group;

  constructor(private readonly _engine: Engine) {}

  private _getOrCreateGroup(): Group | undefined {
    const scene = this._engine.activeScene;
    if (!scene) return undefined;
    if (!this._group) {
      const g = new Group();
      g.name = "engine-debug-log";
      scene.add(g);
      this._group = g;
    }
    return this._group;
  }

  /**
   * Sahneye kucuk bir hata ayiklama mesh'i ekler (kure veya kup).
   * Ornek: Engine.activeEngine?.log("Sphere", worldPosition, worldQuaternion)
   */
  log(
    shape: string,
    position: Vector3 | readonly [number, number, number],
    rotation?: Euler | Quaternion | readonly [number, number, number],
  ): void {
    const group = this._getOrCreateGroup();
    if (!group) return;

    const pos =
      position instanceof Vector3
        ? position.clone()
        : new Vector3(position[0], position[1], position[2]);

    const key = shape.trim().toLowerCase();
    const useSphere = key === "sphere" || key === "spehere" || key === "küre";
    const geom = useSphere
      ? new SphereGeometry(0.08, 16, 12)
      : new BoxGeometry(0.14, 0.14, 0.14);

    const hue = (group.children.length * 0.17) % 1;
    const mat = new MeshBasicMaterial({
      color: new Color().setHSL(hue, 0.85, 0.55),
      depthTest: true,
      transparent: true,
      opacity: 0.9,
    });

    const mesh = new Mesh(geom, mat);
    mesh.name = "engine-debug-" + shape + "-" + group.children.length;
    mesh.userData.engineDebugLog = true;
    mesh.position.copy(pos);

    if (rotation !== undefined) {
      if (rotation instanceof Quaternion) {
        mesh.setRotationFromQuaternion(rotation);
      } else if (rotation instanceof Euler) {
        mesh.rotation.copy(rotation);
      } else {
        mesh.rotation.set(rotation[0], rotation[1], rotation[2], "XYZ");
      }
    }

    group.add(mesh);
    this._engine.dispatchCustomEvent("request-update");
  }

  /** log ile eklenen tum hata ayiklama mesh'lerini ve kaynaklarini kaldirir. */
  clear(): void {
    const g = this._group;
    if (!g) return;
    while (g.children.length > 0) {
      const mesh = g.children[0] as Mesh;
      g.remove(mesh);
      mesh.geometry?.dispose();
      const m = mesh.material;
      if (Array.isArray(m)) m.forEach((x) => x.dispose());
      else (m as MeshBasicMaterial)?.dispose?.();
    }
    g.removeFromParent();
    this._group = undefined;
    this._engine.dispatchCustomEvent("request-update");
  }
}
